import { keepPreviousData, useQuery } from "@tanstack/react-query"
import { LinkProps } from "@tanstack/react-router"
import { Separator } from "design-system/atoms/Separator"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import DateDisplay from "design-system/molecules/DateDisplay"
import {
  Table,
  TableBody,
  TableCell,
  TableEmptySlate,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
} from "design-system/molecules/Table"
import { conversationsResponsesQuery } from "modules/conversations/lib/conversationsResponsesQuery"
import { SentimentBadge } from "modules/shared/components/Miscelanea/SentimentBadge"
import { Pagination } from "modules/shared/components/Pagination"
import { ActionRequestStatusIcon } from "modules/support/components/entities/ActionRequestEntityRow"
import { IssueOccurrenceCountBadge } from "modules/support/components/entities/IssueOccurrenceCountBadge"
import { IssueReopenedBadge } from "modules/support/components/entities/IssueReopenedBadge"
import { IssueStatusBadge } from "modules/support/components/entities/IssueUrgencyIdentifier"
import { QuestionStatusIcon } from "modules/support/components/entities/QuestionEntityRow"
import { actionRequestsQuery } from "modules/support/lib/actionRequestsQuery"
import { supportIssuesQuery } from "modules/support/lib/supportIssuesQuery"
import { supportQuestionsQuery } from "modules/support/lib/supportQuestionsQuery"
import { ReactNode, useState } from "react"
import { useTranslation } from "react-i18next"
import { SearchSchema as ConversationsSearchSchema } from "routes/_base/_auth/conversations/list/route"
import { SearchSchema as SupportSearchSchema } from "routes/_base/_auth/support/list/route"
import { CanonicalTopicFilterBy, ClusterDomain, ClusterDomainEnum } from "schema"

const ITEMS_PER_PAGE = 10
/** Takes the remaining width and lets its content truncate instead of overflowing the panel */
const FLUID_COLUMN_CLASS_NAME = "w-full max-w-0"

type SupportTab = "issues" | "newQuestions" | "actionRequests"

type Props = {
  domain: ClusterDomain
  canonicalTopicId: string
  filterBy?: CanonicalTopicFilterBy
}

type TableProps = {
  canonicalTopicId: string
  campaignId: string | undefined
}

export function TopicItemsTable({ domain, canonicalTopicId, filterBy }: Props) {
  if (!canonicalTopicId) return null

  const table = renderTable({
    domain,
    canonicalTopicId,
    campaignId: filterBy?.campaignId,
    filterBy,
  })
  if (!table) return null

  return (
    <>
      <Separator />
      {table}
    </>
  )
}

function renderTable({
  domain,
  filterBy,
  ...tableProps
}: TableProps & { domain: ClusterDomain; filterBy?: CanonicalTopicFilterBy }) {
  switch (domain) {
    case ClusterDomainEnum.Issue:
      return <TopicIssuesTable {...tableProps} />
    case ClusterDomainEnum.Question:
      return <TopicQuestionsTable {...tableProps} />
    case ClusterDomainEnum.ActionRequest:
      return <TopicActionRequestsTable {...tableProps} />
    case ClusterDomainEnum.VoiceOfCustomer:
      return <TopicResponsesTable {...tableProps} filterBy={filterBy} />
    default:
      return null
  }
}

function TopicIssuesTable({ canonicalTopicId, campaignId }: TableProps) {
  const { t } = useTranslation()
  const [page, setPage] = useState(1)
  const [count, setCount] = useState<number>()

  const { data, isPending } = useQuery({
    ...supportIssuesQuery({
      query: buildSupportEntitiesQuery({ page, canonicalTopicId, campaignId }),
      onSuccess: (response) => setCount(response.pageInfo.count),
    }),
    placeholderData: keepPreviousData,
  })
  const search = buildSupportSearch({ supportTab: "issues", canonicalTopicId, campaignId })

  return (
    <LiteTable
      headers={[
        { label: t("support.issues.table.urgency"), className: "w-px whitespace-nowrap" },
        { label: t("support.issues.table.subject"), className: FLUID_COLUMN_CLASS_NAME },
        {
          label: t("support.issues.table.occurrences"),
          className: "w-px whitespace-nowrap",
          align: "center",
        },
        {
          label: t("support.issues.table.createdAt"),
          className: "w-px whitespace-nowrap",
          align: "right",
        },
      ]}
      rows={data?.map((issue) => ({
        id: issue.id,
        navigate: {
          to: "/support/list/$customerId",
          params: { customerId: issue.customer.id },
          search: {
            ...search,
            conversationId: issue.conversationId ? Number(issue.conversationId) : undefined,
            tab: "issues",
            issueId: issue.id,
          },
        },
        cells: [
          <IssueStatusBadge key="urgency" issue={issue} />,
          <span key="subject" className="flex min-w-0 items-center gap-2">
            <TruncatedText text={issue.subject} />
            <IssueReopenedBadge issue={issue} />
          </span>,
          <IssueOccurrenceCountBadge key="occurrences" count={issue.occurrenceCount} />,
          <DateDisplay key="createdAt" date={issue.createdAt} />,
        ],
      }))}
      isPending={isPending}
      emptyMessage={t("support.issues.table.empty")}
      page={page}
      count={count}
      onPageChange={setPage}
    />
  )
}

function TopicQuestionsTable({ canonicalTopicId, campaignId }: TableProps) {
  const { t } = useTranslation()
  const [page, setPage] = useState(1)
  const [count, setCount] = useState<number>()

  const { data, isPending } = useQuery({
    ...supportQuestionsQuery({
      query: buildSupportEntitiesQuery({ page, canonicalTopicId, campaignId }),
      onSuccess: (response) => setCount(response.pageInfo.count),
    }),
    placeholderData: keepPreviousData,
  })
  const search = buildSupportSearch({ supportTab: "newQuestions", canonicalTopicId, campaignId })

  return (
    <LiteTable
      headers={[
        { label: "", className: "w-px pr-0" },
        { label: t("support.newQuestions.table.summary"), className: FLUID_COLUMN_CLASS_NAME },
        {
          label: t("support.newQuestions.table.createdAt"),
          className: "w-px whitespace-nowrap",
          align: "right",
        },
      ]}
      rows={data?.map((question) => ({
        id: question.id,
        navigate: {
          to: "/support/list/$customerId",
          params: { customerId: question.customer.id },
          search: {
            ...search,
            conversationId: Number(question.conversationId),
            tab: "conversations",
          },
        },
        cells: [
          <QuestionStatusIcon key="status" item={question} />,
          <TruncatedText key="summary" text={question.summary} />,
          <DateDisplay key="createdAt" date={question.createdAt} />,
          ,
        ],
      }))}
      isPending={isPending}
      emptyMessage={t("support.newQuestions.table.empty")}
      page={page}
      count={count}
      onPageChange={setPage}
    />
  )
}

function TopicActionRequestsTable({ canonicalTopicId, campaignId }: TableProps) {
  const { t } = useTranslation()
  const [page, setPage] = useState(1)
  const [count, setCount] = useState<number>()

  const { data, isPending } = useQuery({
    ...actionRequestsQuery({
      query: buildSupportEntitiesQuery({ page, canonicalTopicId, campaignId }),
      onSuccess: (response) => setCount(response.pageInfo.count),
    }),
    placeholderData: keepPreviousData,
  })
  const search = buildSupportSearch({ supportTab: "actionRequests", canonicalTopicId, campaignId })

  return (
    <LiteTable
      headers={[
        { label: "", className: "w-px pr-0" },
        { label: t("support.actionRequests.table.summary"), className: FLUID_COLUMN_CLASS_NAME },
        {
          label: t("support.actionRequests.table.createdAt"),
          className: "w-px whitespace-nowrap",
          align: "right",
        },
      ]}
      rows={data?.map((actionRequest) => ({
        id: actionRequest.id,
        navigate: {
          to: "/support/list/$customerId",
          params: { customerId: actionRequest.customer.id },
          search: {
            ...search,
            conversationId: Number(actionRequest.conversationId),
            tab: "conversations",
          },
        },
        cells: [
          <ActionRequestStatusIcon key="status" status={actionRequest.status} />,
          <TruncatedText key="summary" text={actionRequest.summary} />,
          <DateDisplay key="createdAt" date={actionRequest.createdAt} />,
          ,
        ],
      }))}
      isPending={isPending}
      emptyMessage={t("support.actionRequests.table.empty")}
      page={page}
      count={count}
      onPageChange={setPage}
    />
  )
}

function TopicResponsesTable({
  canonicalTopicId,
  campaignId,
  filterBy,
}: TableProps & { filterBy?: CanonicalTopicFilterBy }) {
  const { t } = useTranslation()
  const [page, setPage] = useState(1)
  const [count, setCount] = useState<number>()

  const { data, isPending } = useQuery({
    ...conversationsResponsesQuery({
      query: {
        page,
        total: ITEMS_PER_PAGE,
        sortBy: { field: "createdAt", order: "desc" },
        filterBy: {
          canonicalId: canonicalTopicId,
          campaignId,
          channel: filterBy?.channel,
          conversationFlow: filterBy?.conversationFlow,
        },
      },
      onSuccess: (response) => setCount(response.pageInfo.count),
    }),
    placeholderData: keepPreviousData,
  })
  const search = ConversationsSearchSchema.parse({
    filterBy: { domain: ClusterDomainEnum.VoiceOfCustomer, canonicalId: canonicalTopicId },
  })

  return (
    <LiteTable
      headers={[
        { label: t("dashboard.tooltip.topicItems.sentiment"), className: "w-px whitespace-nowrap" },
        { label: t("dashboard.tooltip.topicItems.response"), className: FLUID_COLUMN_CLASS_NAME },
        {
          label: t("dashboard.tooltip.topicItems.responseTime"),
          className: "w-px whitespace-nowrap",
          align: "right",
        },
      ]}
      rows={data?.map((response) => ({
        id: response.id,
        navigate: {
          to: "/conversations/list/$customerId",
          params: { customerId: response.customerId },
          search: { ...search, conversationId: Number(response.conversationId) },
        },
        cells: [
          <SentimentBadge key="sentiment" sentiment={response.sentiment} colored />,
          <TruncatedText
            key="response"
            text={response.response ?? response.goalOptionValue ?? "-"}
          />,
          <DateDisplay key="createdAt" date={response.createdAt} />,
        ],
      }))}
      isPending={isPending}
      emptyMessage={t("dashboard.tooltip.topicItems.noResponses")}
      page={page}
      count={count}
      onPageChange={setPage}
    />
  )
}

type LiteTableHeader = { label: string; className?: string; align?: "center" | "right" }

type LiteTableRow = {
  id: string
  navigate: LinkProps
  cells: ReactNode[]
}

function LiteTable({
  headers,
  rows,
  isPending,
  emptyMessage,
  page,
  count,
  onPageChange,
}: {
  headers: LiteTableHeader[]
  rows: LiteTableRow[] | undefined
  isPending: boolean
  emptyMessage: string
  page: number
  count: number | undefined
  onPageChange: (page: number) => void
}) {
  function renderContent() {
    if (isPending || rows === undefined) {
      return <TableSkeleton colSpans={headers.map(() => 1)} />
    }

    if (rows.length === 0) {
      return <TableEmptySlate colSpan={headers.length} message={emptyMessage} />
    }

    return rows.map((row) => (
      <TableRow key={row.id} navigate={row.navigate}>
        {row.cells.map((cell, index) => (
          <TableCell
            key={index}
            className={headers[index]?.className}
            align={headers[index]?.align}
          >
            {cell}
          </TableCell>
        ))}
      </TableRow>
    ))
  }

  return (
    <div className="flex flex-col gap-3">
      <Table>
        <TableHeader>
          <TableRow>
            {headers.map((header, index) => (
              <TableHead key={index} className={header.className} align={header.align}>
                {header.label}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>{renderContent()}</TableBody>
      </Table>
      <Pagination
        currentPage={page}
        totalPages={count === undefined ? undefined : Math.ceil(count / ITEMS_PER_PAGE)}
        onPageChange={onPageChange}
        count={count}
      />
    </div>
  )
}

function TruncatedText({ text }: { text: string }) {
  return (
    <Tooltip>
      <TooltipTrigger className="block max-w-full truncate text-left">{text}</TooltipTrigger>
      <TooltipContent className="max-w-80">{text}</TooltipContent>
    </Tooltip>
  )
}

function buildSupportEntitiesQuery({
  page,
  canonicalTopicId,
  campaignId,
}: TableProps & { page: number }) {
  return {
    page,
    total: ITEMS_PER_PAGE,
    sortBy: { field: "createdAt" as const, order: "desc" as const },
    filterBy: { canonicalId: canonicalTopicId, campaignId },
  }
}

function buildSupportSearch({
  supportTab,
  canonicalTopicId,
  campaignId,
}: TableProps & { supportTab: SupportTab }) {
  return SupportSearchSchema.parse({
    supportTab,
    filterBy: { canonicalId: canonicalTopicId, campaignId },
  })
}
